import { useTranslation } from "react-i18next";
import { NavHashLink } from "react-router-hash-link";
import { useNavigate } from "react-router-dom";

import {
  Button,
  IcoEdit,
  Label,
  TextMessage,
  TextPlain,
  Title,
  TitleH1,
  TitleH2,
  TitleH3,
  TitleH4
} from "components/generic";
import { theme } from "styles/theme";
import {
  ContainerLogin,
  ContainerRedirect,
  ContainerTitleEdit,
  ContainerTitleProfile,
  ContainerValueView,
  DialogWrapper,
  FlexBox,
  GridWrapper
} from "components/containers";
import Range, { IRangeHook } from "components/Range";
import {
  HOST_PROFILE_EDIT_FIRST_ROUTE,
  HOST_PROFILE_EDIT_ROUTE,
  LOGIN_ROUTE,
  MONTHS,
  STUDENT_PROFILE_EDIT_FIRST_ROUTE,
  STUDENT_PROFILE_EDIT_ROUTE,
  STUDENT_ROOMS_ROUTE
} from "utils/constants";

import { arrayLanguages, getNameCountry } from "utils/fetchMapInfo";
import { RoleUser, TPersonDetails } from "interfaces/intarfaces";
import { useContextHospi } from "context/ContextHospi";

import photo_default from "assets/images/empty_photo.webp";
import React, { useEffect, useState } from "react";
import { IcoCalendar } from "components/InputCalendar/styles";
import { UserAPI } from "API/userApi";
import { HostVerify } from "pages/ProfileUserEdit/HostVerified";
import Modal from "components/Modal";
import { TLang } from "assets/data/isoNameLanguage";

export const UnauthorizedUser = () => {
  const navigate = useNavigate();
  const { currentUserRole, setCurrentUserId } = useContextHospi();
  const { t, i18n } = useTranslation(currentUserRole || "student");
  return (
    <ContainerLogin>
      <ContainerRedirect>
        <Title m="0 0 24px 0">{t("unauthorized.title")}</Title>
        <TextMessage>{t("unauthorized.text")}</TextMessage>
      </ContainerRedirect>
      <Button
        main
        fontWeight="400"
        w="100%"
        m="24px 0 0 0"
        onClick={() => {
          setCurrentUserId(undefined);
          navigate(`/${i18n.language}${LOGIN_ROUTE.slice(5)}`);
        }}
      >
        {t("unauthorized.button")}
      </Button>
    </ContainerLogin>
  );
};

export const TitleProfile: React.FC<{
  user: RoleUser;
  title: string;
  fetchUser?: any;
  edit: string;
  style?: React.CSSProperties;
}> = ({ user, title, fetchUser, edit, style }) => {
  const { t } = useTranslation("student");
  return (
    <ContainerTitleProfile style={style}>
      <TitleH1 display="flex" alignItems="center">
        {title}
        {user === "student" && edit === "false" ? (
          <Button main={true} m="0 0 0 20px" onClick={fetchUser}>
            {t("viewProfileBtn")}
          </Button>
        ) : (
          <></>
        )}
      </TitleH1>
      {user === "host" && <VerifiedInfo />}
    </ContainerTitleProfile>
  );
};

export const NoMatchesInfoSection = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("matches");
  return (
    <>
      <TitleH3>{t("student.noMatches.text")}</TitleH3>
      <Button
        main
        h="40px"
        onClick={() =>
          navigate(`/${i18n.language}${STUDENT_ROOMS_ROUTE.slice(5)}`)
        }
      >
        {t("student.noMatches.btnText")}
      </Button>
    </>
  );
};

export const TitleEdit: React.FC<{ keyItem: string }> = ({ keyItem }) => {
  const { currentUserRole, userProfile } = useContextHospi();
  const { t, i18n } = useTranslation(currentUserRole || "student");

  const linkEdit = () => {
    return userProfile?.is_profile_complete
      ? currentUserRole === "host"
        ? `/${i18n.language}${HOST_PROFILE_EDIT_ROUTE.slice(5)}`
        : `/${i18n.language}${STUDENT_PROFILE_EDIT_ROUTE.slice(5)}`
      : currentUserRole === "host"
        ? `/${i18n.language}${HOST_PROFILE_EDIT_FIRST_ROUTE.slice(5)}`
        : `/${i18n.language}${STUDENT_PROFILE_EDIT_FIRST_ROUTE.slice(5)}`;
  };

  return (
    <ContainerTitleEdit>
      <Title m="0 0 4px 0" fontSize="20px">
        {t(
          `${currentUserRole === "host" ? "hostProfile" : "studentProfile"
          }.${keyItem}.title`
        )}
      </Title>
      <NavHashLink to={`${linkEdit()}#${keyItem}`}>
        {/*<NavHashLink to={`/${i18n.language}${linkEdit().slice(5)}#${keyItem}`}>*/}
        <IcoEdit size="1.1em" />
        <Label cursor="pointer" p="0 0 0 4px">
          Edit
        </Label>
      </NavHashLink>
    </ContainerTitleEdit>
  );
};

const StyleImage = { width: 120, height: 120, borderRadius: "50%" };

export const PersonalDetails: React.FC<TPersonDetails> = ({
  first_name,
  last_name,
  birth_date,
  birth_country,
  phone_code,
  phone_number,
  thumbnail_url
}) => {
  const [showDefault, setShowDefault] = useState(false);
  const [urlImage, setUrlImage] = useState(
    thumbnail_url + `?${new Date().toString()}`
  );

  const { i18n } = useTranslation();

  useEffect(() => {
    let timer1 = setTimeout(() => {
      setUrlImage(thumbnail_url + `?${new Date().toString()}`);
    }, 2000);

    return () => {
      clearTimeout(timer1);
    };
  }, [thumbnail_url]);

  const currLang = i18n.language.toUpperCase() as TLang;

  return (
    <ContainerValueView style={{ marginBottom: 0 }}>
      <TitleEdit keyItem="personalDetails" />
      <FlexBox justify="space-between" align="flex-start" padding="8px 0 0 0">
        <FlexBox direction="column" align="flex-start">
          <Label p="0" style={{ wordBreak: "break-all" }}>{`${first_name ?? ""
            } ${last_name ?? ""}`}</Label>
          {birth_date && <Label p="4px 0 0 0">{`${birth_date}`}</Label>}
          {birth_country && (
            <Label p="4px 0 0 0">{`${getNameCountry(
              birth_country,
              currLang
            )}`}</Label>
          )}
          <Label p="4px 0 0 0" style={{ wordBreak: "break-all" }}>{`${phone_code ?? ""
            } ${phone_number ?? ""}`}</Label>
        </FlexBox>
        <picture>
          {showDefault ? (
            <img src={photo_default} alt="" style={StyleImage} />
          ) : (
            <img
              src={urlImage}
              alt=""
              onError={() => setShowDefault(true)}
              onLoad={() => setShowDefault(false)}
              style={StyleImage}
            />
          )}
        </picture>
      </FlexBox>
    </ContainerValueView>
  );
};

export const AboutMeView: React.FC<{
  genderUser?: string;
  studyStudent?: string;
  spokenLanguage?: string[];
  expirenceStudent?: string[];
  descriptionUser?: string;
}> = ({
  genderUser,
  studyStudent,
  spokenLanguage,
  expirenceStudent,
  descriptionUser
}) => {
    return (
      <GridWrapper gap="8px">
        <Label p="0">
          {genderUser
            ? genderUser[0].toUpperCase() +
            genderUser?.substring(1).replaceAll("_", " ")
            : ""}
        </Label>
        {studyStudent && (
          <Label p="0" style={{ wordBreak: "break-word" }}>
            {studyStudent || ""}
          </Label>
        )}
        {spokenLanguage && <SpokenLanguages items={spokenLanguage || ""} />}
        {expirenceStudent && (
          <ul>
            {expirenceStudent.map((item) => (
              <li key={item} style={{ marginTop: "4px" }}>
                <Label p="0">{item}</Label>
              </li>
            ))}
          </ul>
        )}
        <Label p="0" style={{ wordBreak: "break-word" }}>
          {descriptionUser || ""}
        </Label>
      </GridWrapper>
    );
  };

export const SpokenLanguages: React.FC<{ items: string[] }> = ({ items }) => {
  const { i18n } = useTranslation();
  const curLang: TLang = (i18n.language.toUpperCase() as TLang) ?? "GB";
  return (
    <FlexBox direction="row" align="flex-start" justify="flex-start">
      {arrayLanguages(items, curLang).map((item) => (
        <Label
          bgColor={theme.colors.secondary}
          key={item}
          style={{ border: `1px solid ${theme.colors.border}` }}
          p="4px"
          m="4px 4px 0 0"
        >
          {item}
        </Label>
      ))}
    </FlexBox>
  );
};

export interface ISocialPreferences {
  roleUser: RoleUser;
  privacy: IRangeHook;
  newInTown: IRangeHook;
  muted?: boolean;
}

export const SocialPreferences: React.FC<ISocialPreferences> = ({
  roleUser,
  privacy,
  newInTown,
  muted = true
}) => {
  const { currentUserRole } = useContextHospi();
  const { t } = useTranslation(currentUserRole);
  return (
    <>
      <Range
        {...privacy}
        disabled={muted}
        title={t(`${roleUser}Profile.socialPreferences.form.privacy.title`)}
        markers={t(`${roleUser}Profile.socialPreferences.form.privacy.items`, {
          returnObjects: true
        })}
        p="0 0 24px 0"
        min={1}
        max={100}
      />
      <Range
        {...newInTown}
        disabled={muted}
        title={t(`${roleUser}Profile.socialPreferences.form.newInTown.title`)}
        markers={t(
          `${roleUser}Profile.socialPreferences.form.newInTown.items`,
          { returnObjects: true }
        )}
        min={1}
        max={100}
      />
    </>
  );
};

interface IDateLine {
  start: string;
  end: string;
  ico?: boolean;
  context?: string;  // add this line
}

export const DateLine: React.FC<
  IDateLine & React.HTMLAttributes<HTMLDivElement>
> = ({ start, end, ico, context = "createProfile", ...props }) => {
  const { t } = useTranslation();
  const [startY, startM, startD] = start.slice(0, 10).split("-");
  const [endY, endM, endD] = end.slice(0, 10).split("-");

  const startDate = `${startD} ${t(
    `calendar.months.${MONTHS[Number(startM) - 1]}`
  )
    .toLocaleLowerCase()
    .slice(0, 3)}
${startY}`;

  const lineDate = `${startDate} - ${endD} ${t(
    `calendar.months.${MONTHS[Number(endM) - 1]}`
  )
    .toLocaleLowerCase()
    .slice(0, 3)}
${endY}`;

  const defaultText = context === 'profileOverview' ? t("noDatesSelected") : t("chooseDate");
  const endDateText = end === "1970-01-01" ? `${startDate} - ${t("noEndDate")}` : lineDate;

  return (
    <TitleH4 {...props}>
      {ico && (
        <IcoCalendar
          size="1em"
          fontColor
          style={{ marginRight: "0.5em", cursor: "default" }}
        />
      )}
      {start && end ? endDateText : defaultText}
    </TitleH4>
  );
};

export const VerifiedInfo: React.FC = () => {
  const { userProfile, updateProfile } = useContextHospi();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(0);
  const { t, i18n } = useTranslation("host");

  const sendRequest = async () => {
    if (
      userProfile?.user_type === "host" &&
      userProfile.verification_status === "unverified"
    )
      if (userProfile.is_profile_complete) {
        const response = await UserAPI.requestVerification(
          userProfile?.user_id
        );

        updateProfile(response);
      } else
        navigate(`/${i18n.language}${HOST_PROFILE_EDIT_FIRST_ROUTE.slice(5)}`);
    setShowModal(0);
  };
  return (
    <>
      {userProfile?.user_type && userProfile?.user_type === "host" ? (
        <HostVerify
          verification_status={userProfile.verification_status}
          onClick={() =>
            userProfile.verification_status === "unverified" &&
            setShowModal(userProfile.is_profile_complete ? 10 : 20)
          }
        />
      ) : (
        <span />
      )}
      {showModal !== 0 && (
        <Modal isShow={!!showModal} handleClose={() => setShowModal(0)}>
          <DialogWrapper>
            <TitleH2>{t(`hostProfile.requestVerified.title`)}</TitleH2>
            <TextPlain
              style={{
                whiteSpace: "pre-line",
                wordBreak: "break-word",
                marginTop: "24px"
              }}
            >
              {showModal === 10 &&
                t(`hostProfile.requestVerified.text`, {
                  joinArrays: "\n\n"
                })}
              {showModal === 20 &&
                t(`hostProfile.requestVerified.message`, {
                  joinArrays: "\n\n"
                })}
            </TextPlain>

            <div className="buttons">
              <Button normal onClick={() => setShowModal(0)}>
                {showModal === 10 &&
                  t(`hostProfile.requestVerified.buttonSkip`)}
                {showModal === 20 &&
                  t(`hostProfile.requestVerified.buttonCancel`)}
              </Button>
              <Button main onClick={sendRequest}>
                {showModal === 10 &&
                  t(`hostProfile.requestVerified.buttonRequest`)}
                {showModal === 20 &&
                  t(`hostProfile.requestVerified.buttonGoEditProfile`)}
              </Button>
            </div>
          </DialogWrapper>
        </Modal>
      )}
    </>
  );
};
